import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router';
import { usePermissions } from '@/auth/permissions';
import { MENU, type MenuItem } from './menuConfig';

function useIsActive() {
  const location = useLocation();
  return (path?: string) => {
    if (!path) return false;
    const [pathname, query] = path.split('?');
    if (query) {
      const expected = new URLSearchParams(query);
      const current = new URLSearchParams(location.search);
      return location.pathname === pathname && [...expected].every(([k, v]) => current.get(k) === v);
    }
    return location.pathname === pathname || location.pathname.startsWith(`${pathname}/`);
  };
}

const itemSx = {
  mx: 1,
  borderRadius: 2,
  '&.Mui-selected': {
    color: 'primary.main',
    bgcolor: 'action.selected',
    boxShadow: (theme: { palette: { primary: { main: string } } }) => `inset 3px 0 0 ${theme.palette.primary.main}`,
    '& .MuiListItemIcon-root': { color: 'primary.main' },
  },
} as const;

function MenuEntry({ item, collapsed, onNavigate }: { item: MenuItem; collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation('menu');
  const isActive = useIsActive();
  const { can } = usePermissions();
  const childActive = item.children?.some((c) => isActive(c.path)) ?? false;
  const [open, setOpen] = useState(childActive);

  if (item.permission && !can(item.permission)) return null;
  const Icon = item.icon;
  const label = t(item.key);

  if (item.children) {
    return (
      <>
        <Tooltip title={collapsed ? label : ''} placement="right">
          <ListItemButton sx={itemSx} selected={collapsed && childActive} onClick={() => setOpen((o) => !o)}>
            {Icon ? (
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
            ) : null}
            {!collapsed && <ListItemText primary={label} />}
            {!collapsed && (open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />)}
          </ListItemButton>
        </Tooltip>
        <Collapse in={open && !collapsed} unmountOnExit>
          <List disablePadding dense>
            {item.children.map((child) => (
              <ListItemButton
                key={child.key}
                component={RouterLink}
                to={child.path ?? '#'}
                selected={isActive(child.path)}
                onClick={onNavigate}
                sx={{ ...itemSx, pl: 7 }}
              >
                <ListItemText primary={t(child.key)} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <Tooltip title={collapsed ? label : ''} placement="right">
      <ListItemButton
        component={RouterLink}
        to={item.path ?? '#'}
        selected={isActive(item.path)}
        onClick={onNavigate}
        sx={itemSx}
      >
        {Icon ? (
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Icon fontSize="small" />
          </ListItemIcon>
        ) : null}
        {!collapsed && <ListItemText primary={label} />}
      </ListItemButton>
    </Tooltip>
  );
}

export function SideMenu({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation('menu');
  const { can } = usePermissions();
  return (
    <Box component="nav" aria-label="main" sx={{ overflowY: 'auto', flex: 1, pb: 2 }}>
      {MENU.filter((s) => !s.permission || can(s.permission)).map((section) => {
        const visible = section.items.filter((i) => !i.permission || can(i.permission));
        if (visible.length === 0) return null;
        return (
          <List
            key={section.key}
            dense
            subheader={
              collapsed ? undefined : (
                <ListSubheader
                  disableSticky
                  sx={{ bgcolor: 'transparent', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 12, lineHeight: '36px', mt: 1 }}
                >
                  {t(section.key)}
                </ListSubheader>
              )
            }
          >
            {visible.map((item) => (
              <MenuEntry key={item.key} item={item} collapsed={collapsed} onNavigate={onNavigate} />
            ))}
          </List>
        );
      })}
    </Box>
  );
}
